import classNames from "classnames";
import parse from "html-react-parser";
import { debounce as _debounce } from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import AlgoliaProvider from "@repo/shopifyV2/algolia/AlgoliaProvider";
import { trackAlgoliaClickOn } from "@repo/shopifyV2/algolia/insights";
import { useIsAlgoliaDown } from "@repo/shopifyV2/algolia/status";
import useAlgoliaQuerySuggestions from "@repo/shopifyV2/algolia/useAlgoliaQuerySuggestions";
import useCheckDevice from "@repo/shopifyV2/hooks/useCheckDevice";
import type { IPredictiveSearchProduct } from "@repo/shopifyV2/interface/search";
import type { SearchQuerySuggestion } from "@repo/shopifyV2/types/storefront.types";

import Button from "@atoms/Button";
import Icon from "@atoms/Icon";
import Image from "@atoms/Image";
import Input from "@atoms/Input";
import Link from "@atoms/Link";

import { NOTIFICATIONS } from "@const/notifications";
import { URLS } from "@const/urls";

import useAlgoliaPredictive from "@hooks/algolia/useAlgoliaPredictive";
import useCheckLocation from "@hooks/useCheckLocation";

import { ALGOLIA_ENABLED } from "@lib/algolia";

import useSearch from "@queries/useSearch";

import { useLayoutStore } from "@store/layout";

interface IFormData {
  search: string;
}

/** highlighted 는 입력과 일치한 부분이 <b> 로 감싸인 HTML(Algolia Query Suggestions). */
type TSuggestion = Pick<SearchQuerySuggestion, "text"> & { highlighted?: string };

/** 기존 Shopify 예측검색 변수. 추천 검색어만 필요할 때는 types 만 바꿔 쓴다. */
const PREDICTIVE_VARIABLES = {
  limit: 10,
  limitScope: "EACH",
  searchableFields: ["TITLE", "VENDOR", "PRODUCT_TYPE", "BODY"],
  types: ["QUERY", "PRODUCT"],
  unavailableProducts: "LAST",
};

interface IHeaderSearchViewProps {
  suggestions: TSuggestion[];
  products: IPredictiveSearchProduct[];
  loading: boolean;
  onSearch: (value: string) => unknown;
}

/** 헤더 검색 화면. 데이터 출처(Shopify / Algolia)와 무관하게 같은 화면을 그린다. */
function HeaderSearchView({ suggestions, products: searchProducts, loading: isLoading, onSearch }: IHeaderSearchViewProps) {
  const navigate = useNavigate();

  const { openToast, openSearch } = useLayoutStore();

  const { isPC } = useCheckDevice();
  const { isDanielJohnston } = useCheckLocation();

  const { register, handleSubmit, reset, setFocus, watch, getValues } = useForm<IFormData>();

  const { search } = watch();

  useEffect(() => {
    setFocus("search");
  }, []);

  const handleSearchValue = () => onSearch(getValues("search") ?? "");

  const onSubmit = handleSubmit(
    () => {
      if (search) {
        navigate(`${URLS.SEARCH}?q=${search}`);
        reset();
        openSearch(false);
      }
    },
    () => {
      openToast(NOTIFICATIONS.ERROR_SEARCH);
    },
  );

  return (
    <div
      className={classNames(
        "pc:top-0 pc:h-auto pc:w-1/3 pc:fixed pc:bg-white pc:pt-4 pc:p-5 top-12 right-0 z-60 flex max-h-dvh w-full flex-col gap-10 lg:w-1/4 dark:bg-black dark:text-white",
        isDanielJohnston ? "dark:bg-dj-black" : "dark:bg-black",
      )}
    >
      <div className="flex flex-col gap-5">
        {isPC ? (
          <div className="flex justify-between">
            <span>Search Items</span>
            <Button
              as="text"
              className="pc:visible invisible"
              onClick={() => {
                openSearch(false);
              }}
            >
              Close
            </Button>
          </div>
        ) : null}
        <form
          className={classNames("relative flex flex-col gap-2", isDanielJohnston ? "dark:bg-dj-black" : "dark:bg-black")}
          onSubmit={onSubmit}
        >
          <Input
            {...register("search", {
              required: true,
              onChange: _debounce(() => {
                void handleSearchValue();
              }, 200),
            })}
            id="search"
            innerClassName="text-[20px]!"
            placeholder="Type to search items"
          >
            {search ? (
              <Button
                as="wrapper"
                className="absolute top-1/2 right-0 -translate-y-1/2"
                onClick={() => {
                  reset();
                }}
              >
                <Icon className="text-black dark:text-white" name="Refresh" />
              </Button>
            ) : null}
          </Input>
          {search && isLoading ? (
            <span className="text-sm">Loading...</span>
          ) : (
            <>
              {search && !suggestions.length && !searchProducts.length ? (
                <span className="text-sm">{`No Result for "${search}"`}</span>
              ) : null}
            </>
          )}
        </form>
      </div>

      {search && (suggestions.length || searchProducts.length) ? (
        <section
          className={classNames(
            "pc:max-h-full pc:relative pc:top-0 pc:px-0 scrollbar-hide absolute top-24 left-0 flex max-h-[calc(100dvh-10.75rem)] w-full flex-col gap-10 overflow-y-scroll bg-white px-4",
            isDanielJohnston ? "dark:bg-dj-black" : "dark:bg-black",
          )}
        >
          {suggestions.length ? (
            <div className="flex flex-col gap-2.5">
              <span className="text-title">Suggestions</span>
              <div className="grid grid-cols-2 gap-y-2">
                {suggestions.map((result) => (
                  <Link
                    as="text"
                    className="capitalize"
                    href={`${URLS.SEARCH}?q=${result.text}`}
                    key={result.text}
                    onClick={() => {
                      openSearch(false);
                    }}
                  >
                    {result.highlighted ? parse(result.highlighted) : result.text}
                  </Link>
                ))}
              </div>
            </div>
          ) : null}

          {searchProducts.length ? (
            <div className="flex flex-col gap-2.5">
              <span className="text-title">Products</span>
              <div className="tnt-gap-x pc:grid-cols-3 pc:gap-y-10 grid grid-cols-2 gap-x-2 gap-y-2">
                {searchProducts
                  .filter((result) => !result.vendor.includes("GWP"))
                  .map((result) => (
                    <Link
                      as="wrapper"
                      className="pc:flex-col flex! h-fit gap-2.5"
                      href={`${URLS.PRODUCTS}/${result.handle}`}
                      key={result.id}
                      onClick={() => {
                        trackAlgoliaClickOn(result);
                        openSearch(false);
                      }}
                    >
                      <Image
                        className="pc:w-auto pc:min-w-auto h-fit w-20 min-w-20"
                        height={result.featuredImage?.height || 1707}
                        src={result.featuredImage?.url}
                        width={result.featuredImage?.width || 1280}
                      />
                      <span className="text-left">{result.title}</span>
                    </Link>
                  ))}
              </div>
              <div className="sticky bottom-0 flex gap-2 pt-2.5">
                {!isPC && (
                  <Button
                    as="square"
                    className={classNames(isDanielJohnston ? "dark:bg-dj-black" : "bg-white dark:bg-black")}
                    onClick={() => {
                      reset();
                    }}
                    variant="secondary"
                  >
                    Close
                  </Button>
                )}
                <Button
                  as="square"
                  className="w-full"
                  onClick={() => {
                    if (search) {
                      navigate(`${URLS.SEARCH}?q=${search}`);
                      reset();
                      openSearch(false);
                    }
                  }}
                >
                  {`Result for "${search}"`}
                </Button>
              </div>
            </div>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}

/** 기존 Shopify 예측검색. Algolia 를 쓰지 않는 환경과 Algolia 장애 시 사용한다. */
function ShopifyHeaderSearch() {
  const { fetchPredictive } = useSearch();

  const [suggestions, setSuggestions] = useState<SearchQuerySuggestion[]>([]);
  const [searchProducts, setSearchProducts] = useState<IPredictiveSearchProduct[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const handleSearch = async (value: string) => {
    if (!value) return;

    setIsLoading(true);
    // 실패 토스트는 useSearch(shopifyV2) 가 띄운다. 여기서는 로딩만 풀어 준다
    const data = await fetchPredictive({ ...PREDICTIVE_VARIABLES, query: value }).catch(() => undefined);
    setIsLoading(false);

    if (!data) return;

    setSearchProducts(data.predictiveSearch?.products?.filter((node: IPredictiveSearchProduct) => node?.hidden?.value !== "true") || []);
    setSuggestions(data.predictiveSearch?.queries || []);
  };

  return <HeaderSearchView loading={isLoading} onSearch={handleSearch} products={searchProducts} suggestions={suggestions} />;
}

/**
 * Algolia 예측검색(tune 과 같은 방식).
 *
 * - 상품: Algolia 인덱스
 * - 추천 검색어: Algolia Query Suggestions 인덱스(shopify_products_query_suggestions). 인덱스가 없으면 비어 있다.
 * - 상품이 0건이면 가장 위 추천 검색어로 대신 찾아 보여 준다(오타 · 부분 입력 보정).
 */
function AlgoliaHeaderSearch() {
  const { search, products, loading } = useAlgoliaPredictive();

  const [keyword, setKeyword] = useState("");
  // facet 으로 만든 추천은 인기도가 없어 알파벳 역순으로 오므로, 상품 결과가 많은 순으로 보여 준다.
  const querySuggestions = useAlgoliaQuerySuggestions(keyword, { sortByHits: true });

  /** 같은 추천 검색어로 반복해서 대신 찾지 않도록 마지막으로 쓴 값을 기억한다. */
  const fallbackQuery = useRef("");

  useEffect(() => {
    if (!keyword || loading || products.length > 0) return;

    const topSuggestion = querySuggestions[0]?.query;
    if (!topSuggestion || topSuggestion === keyword || topSuggestion === fallbackQuery.current) return;

    fallbackQuery.current = topSuggestion;
    search(topSuggestion);
  }, [keyword, loading, products.length, querySuggestions]);

  const handleSearch = (value: string) => {
    fallbackQuery.current = "";
    setKeyword(value);
    search(value);
  };

  const suggestions = keyword ? querySuggestions.map((item) => ({ text: item.query, highlighted: item.highlighted })) : [];

  return <HeaderSearchView loading={loading} onSearch={handleSearch} products={products} suggestions={suggestions} />;
}

export default function HeaderSearch() {
  const isAlgoliaDown = useIsAlgoliaDown();

  if (!ALGOLIA_ENABLED || isAlgoliaDown) return <ShopifyHeaderSearch />;

  return (
    <AlgoliaProvider>
      <AlgoliaHeaderSearch />
    </AlgoliaProvider>
  );
}
